import req from '../../api/request'

export interface OrgSearchTeam {
  id: string
  name: string
  nameFull: string
  imageUrl: string | null
  organizationId: string | null
  isPlaceholder: boolean
}

export interface SearchOrgTeamsOptions {
  /** Owning organization — the default, narrower scope. */
  orgId: string
  /** Root competition, used only by the root-organization scope. */
  rootScheduleId: string
  query: string
  /** True searches the whole root organization tree instead of `orgId`. */
  isGlobal: boolean
  page?: number
  /** Narrows results to teams of the competition's sport. */
  sportId?: string | null
  genericSportId?: string | null
  signal?: AbortSignal
}

export interface OrgTeamSearchPage {
  teams: OrgSearchTeam[]
  currentPage: number
  totalPages: number
}

const str = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value)

/**
 * The two scopes the legacy "Search All Teams In Root Organization" checkbox
 * switched between. Kept identical so Operations returns the same teams the
 * legacy Setup tab did.
 */
export const orgTeamSearchSlug = (
  orgId: string,
  rootScheduleId: string,
  isGlobal: boolean
): string =>
  isGlobal
    ? `schedules/${rootScheduleId}/teams/rootsearch`
    : `organizations/${orgId}/teams`

const toTeam = (raw: Record<string, unknown>): OrgSearchTeam => {
  const image = raw.image as { full_path?: string } | string | null | undefined

  return {
    id: str(raw.id),
    name: str(raw.name),
    nameFull: str(raw.name_full) || str(raw.name),
    imageUrl:
      typeof image === 'string'
        ? image || null
        : (image?.full_path ?? null) || null,
    organizationId: raw.organization_id ? str(raw.organization_id) : null,
    isPlaceholder: raw.is_placeholder === true,
  }
}

/**
 * One page of the legacy team search.
 *
 * Both slugs return the standard `{ data, meta: { pagination } }` envelope. The
 * raw rows are mapped to a flat shape here so the UI never touches snake_case
 * API fields.
 */
export const searchOrgTeams = async ({
  orgId,
  rootScheduleId,
  query,
  isGlobal,
  page = 1,
  sportId,
  genericSportId,
  signal,
}: SearchOrgTeamsOptions): Promise<OrgTeamSearchPage> => {
  const params = [
    `page=${page}`,
    sportId ? `sport_id=${encodeURIComponent(sportId)}` : '',
    genericSportId
      ? `generic_sport_id=${encodeURIComponent(genericSportId)}`
      : '',
    query ? `query=${encodeURIComponent(query)}` : '',
  ]
    .filter(Boolean)
    .join('&')

  const slug = orgTeamSearchSlug(orgId, rootScheduleId, isGlobal)
  const response = await req(`/${slug}?${params}`, { signal })

  const rows = Array.isArray(response?.data)
    ? (response.data as Array<Record<string, unknown>>)
    : []
  const pagination = response?.meta?.pagination

  return {
    teams: rows.map(toTeam),
    currentPage: Number(pagination?.current_page ?? page),
    totalPages: Number(pagination?.total_pages ?? 1),
  }
}
